import { createFileRoute } from "@tanstack/react-router";
import { LegalPage, Note, Section } from "@/components/legal-page";

export const Route = createFileRoute("/cookies")({
  head: () => ({
    meta: [
      { title: "Xamseen — Cookies & local storage" },
      {
        name: "description",
        content:
          "Xamseen uses only the storage it needs to work: a sign-in session and a few local preferences. No advertising cookies, ad pixels or third-party tracking SDKs.",
      },
      { property: "og:title", content: "Xamseen — Cookies & local storage" },
      { property: "og:description", content: "Exactly what Xamseen stores on your device, and why." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://xamseen.com/cookies" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://xamseen.com/cookies" }],
  }),
  component: CookiesPage,
});

const TOC = [
  { id: "summary", label: "Summary" },
  { id: "what", label: "What Xamseen stores" },
  { id: "third-party", label: "Third-party requests" },
  { id: "control", label: "How to control it" },
];

function CookiesPage() {
  return (
    <LegalPage
      title="Cookies & local storage"
      intro="Xamseen stores the minimum needed to keep you signed in and remember your preferences. There is nothing here for advertising, because Xamseen does not run advertising."
      toc={TOC}
    >
      <Note>
        No advertising cookies. No ad pixels. No third-party analytics or tracking SDKs embedded in Xamseen. Because we use
        only strictly necessary storage and your own saved preferences, Xamseen does not need to interrupt you with a
        tracking consent banner.
      </Note>

      <Section id="summary" title="Summary">
        <p>
          Two kinds of storage are in play: a <strong>session</strong> so the service knows you are signed in, and a small
          number of <strong>preferences</strong> saved in your browser so the app looks and behaves the way you left it.
          Both stay on your device until they expire or you clear them.
        </p>
      </Section>

      <Section id="what" title="What Xamseen stores">
        <div className="overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-surface text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-semibold">What</th>
                <th className="px-4 py-3 font-semibold">Purpose</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Lifetime</th>
              </tr>
            </thead>
            <tbody className="align-top">
              <tr className="border-t border-border">
                <td className="px-4 py-3 font-medium">Authentication session</td>
                <td className="px-4 py-3 text-foreground/80">
                  Keeps you signed in and authorises your requests. Without it you would have to sign in on every page.
                </td>
                <td className="px-4 py-3 text-muted-foreground">Strictly necessary</td>
                <td className="px-4 py-3 text-muted-foreground">Until sign-out or expiry</td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-4 py-3 font-medium">Theme preference</td>
                <td className="px-4 py-3 text-foreground/80">
                  Remembers whether you chose light, dark or system appearance, and applies it before the page paints.
                </td>
                <td className="px-4 py-3 text-muted-foreground">Preference</td>
                <td className="px-4 py-3 text-muted-foreground">Until you clear it</td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-4 py-3 font-medium">Interface state</td>
                <td className="px-4 py-3 text-foreground/80">
                  Small things like dismissed notices and your last selected feed view.
                </td>
                <td className="px-4 py-3 text-muted-foreground">Preference</td>
                <td className="px-4 py-3 text-muted-foreground">Until you clear it</td>
              </tr>
              <tr className="border-t border-border">
                <td className="px-4 py-3 font-medium">Offline app cache</td>
                <td className="px-4 py-3 text-foreground/80">
                  When Xamseen is installed as an app, a service worker caches the interface so it opens on a poor
                  connection.
                </td>
                <td className="px-4 py-3 text-muted-foreground">Strictly necessary</td>
                <td className="px-4 py-3 text-muted-foreground">Until updated or uninstalled</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="third-party" title="Third-party requests">
        <p>
          The civic map loads map tiles and place lookups from a mapping provider. Those requests are made by your browser
          directly to that provider, so it necessarily sees your IP address and the map area you are viewing, and it applies
          its own policies to that request. This is required to draw a map; it is not tracking that we add.
        </p>
        <p>
          Beyond that, Xamseen does not embed third-party scripts for advertising, analytics or social tracking.
        </p>
      </Section>

      <Section id="control" title="How to control it">
        <p>
          You can clear cookies and site data for Xamseen at any time in your browser settings, and you can block them
          entirely. Blocking the authentication session will sign you out and prevent sign-in, since that storage is what
          makes an account work. Clearing preferences simply resets appearance and interface state to their defaults.
        </p>
        <p>
          If Xamseen is installed as an app on your device, uninstalling it removes its offline cache.
        </p>
      </Section>
    </LegalPage>
  );
}
